import {
  DEMOGRAPHIC_ANSWER_KEYS,
  QUESTION_NAMES,
  QUESTION_TYPES,
} from 'client-portal/utils/prescreener/constants';
import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';

export default class PrescreenerValidationService extends Service {
  @service store;
  @service prescreener;

  validationModels = {};
  answers = {};

  @tracked forceValidation = false;

  getValidatedAnswerModel(question, clientType = '') {
    let { name } = question;

    let model = this.getCachedValidationModel(name, clientType);
    if (model) return model;

    const value = this.ensureAnswersFromValues(question, clientType);
    const validationModel = this.createValidationModelForQuestion(question, value, clientType);

    return this.createCachedValidationModel(name, clientType, validationModel);
  }

  createCachedValidationModel(name, clientType, validationModel) {
    if (clientType) {
      if (!this.validationModels[name]) {
        this.validationModels[name] = {};
      }
      this.validationModels[name][clientType] = validationModel;
    } else {
      this.validationModels[name] = validationModel;
    }
    return validationModel;
  }

  getCachedValidationModel(name, clientType) {
    if (clientType) {
      if (this.validationModels[name]?.[clientType]) {
        return this.validationModels[name][clientType];
      }
    } else if (this.validationModels[name]) {
      return this.validationModels[name];
    }
    return null;
  }

  createValidationModelForQuestion(question, value, clientType = '') {
    return this.store.createRecord('prescreener/answer-validation', {
      ...question,
      value,
      clientType,
    });
  }

  ensureAnswersFromValues(question, clientType = '') {
    let { name } = question;
    const value = this.getInitialValue(question);
    if (value !== null) {
      this.answers[name] = clientType ? { [clientType]: value } : value;
    }
    return value;
  }

  updateAnswers(question, value, clientType = '') {
    let { name } = question;
    if (clientType) {
      if (!this.answers[name]) {
        this.answers[name] = {};
      }
      this.answers[name][clientType] = value;
    } else {
      this.answers[name] = value;
    }
  }

  getInitialValue(question) {
    if (question.type === QUESTION_TYPES.SELECT && question.options) {
      const selectedOption = this.#findSelectedOption(question.options);
      if (selectedOption) {
        return selectedOption.value;
      }
    }
    return null;
  }

  isAnswerDemographic(answer) {
    return DEMOGRAPHIC_ANSWER_KEYS.find(key => Object.prototype.hasOwnProperty.call(answer, key));
  }

  hasValue(input) {
    if (typeof input === 'string') {
      return input.trim() !== '';
    }
    return input !== null && input !== undefined;
  }

  get answersForAnswerModel() {
    return Object.entries(this.answers).reduce((acc, [name, answer]) => {
      if (this.hasValue(answer)) {
        acc = this.setAnswerToAccumulator(acc, name, answer);
      }
      return acc;
    }, {});
  }

  getDemographicAnswer(acc, name, answer) {
    let demographicAnswer = [answer.client || null];
    if (answer.partner || answer.contact) {
      demographicAnswer = [...demographicAnswer, answer.partner || answer.contact || null];
    }
    if (demographicAnswer.filter(Boolean).length) {
      acc[name] = demographicAnswer;
    }
    return acc;
  }

  setAnswerToAccumulator(acc, name, answer) {
    if (this.isAnswerDemographic(answer)) {
      acc = this.getDemographicAnswer(acc, name, answer);
    } else {
      acc[name] = answer;
    }
    return acc;
  }

  #findSelectedOption(options) {
    for (const option of options) {
      if (option.selected) {
        return option;
      }
      if (option.options) {
        const selected = this.#findSelectedOption(option.options);
        if (selected) return selected;
      }
    }
    return null;
  }

  updateAnswerModel(question, value, clientType = '') {
    const validationModel = this.getValidatedAnswerModel(question, clientType);
    validationModel.set('value', value);

    if (question.name === QUESTION_NAMES.INSURANCE_CARD_UPLOAD) return;
    this.updateAnswers(question, value, clientType);
  }

  validateAll() {
    const allAnswers = Object.values(this.validationModels);
    this.forceValidation = true;
    return this.validateAnswerModels(allAnswers);
  }

  validateCurrentCategory(currentCategory) {
    if (!currentCategory) return true;

    this.forceValidation = true;

    const categoryAnswers = this.getAnswersForCategory(currentCategory);
    return this.validateAnswerModels(categoryAnswers);
  }

  getAnswersForCategory(category) {
    return Object.values(this.validationModels).filter(model => model.category === category);
  }

  validateAnswerModels(answerModels) {
    for (const answerModel of answerModels) {
      if (answerModel.validations) {
        if (answerModel.validations.isInvalid) {
          return false;
        }
      } else {
        if (!this.validateAnswerModels(Object.values(answerModel))) {
          return false;
        }
      }
    }
    return true;
  }

  clearValidationModels() {
    this.validationModels = {};
    this.answers = {};
  }

  resetValidation() {
    this.forceValidation = false;
  }
}
